import { useAtom } from 'jotai';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { loadingGif } from '../assets/images';
import { reservationDataAtom } from '../jotai-atoms/reservation-data.atom';
import { userAtom } from '../jotai-atoms/user.atom';
import { getLiveClassShiftListItemsFromRawData } from '../models/LiveClassShiftCollection';
import { LiveClassBookingService } from '../services/liveclass-booking';
import { LiveClassShiftList } from '../stories/LiveClassShiftList';
import { LiveClassShiftListItemProps } from '../stories/LiveClassShiftListItem';
import { Panel } from '../stories/Panel';
import { DaysCarousel } from './DaysCarousel';
import { ReservationDashboard } from './ReservationDashboard';

export const ReservationWidget: React.FC = () => {
  const __ = useTranslation().t;

  const [reservationData] = useAtom(reservationDataAtom);
  const [user] = useAtom(userAtom);

  const hasNoPrivateClassesAvailable =
    !!user?.subscriptionInfo?.hasSubscriptionTypePrivate &&
    user?.subscriptionInfo?.privateSubscriptionAvailable === 0;
  const hasFinishedAllLevelClasses =
    reservationData?.numberOfCompletedLiveClasses === reservationData?.totalLiveClasses;

  const [liveClassShiftListItems, setLiveClassShiftListItems] = useState<
    LiveClassShiftListItemProps[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showNoPrivateClassesAvailableAlert, setShowNoPrivateClassesAvailableAlert] =
    useState<boolean>(hasNoPrivateClassesAvailable);
  const [showFinishedAllLevelClassesAlert, setShowFinishedAllLevelClassesAlert] = useState<boolean>(
    hasFinishedAllLevelClasses,
  );

  useEffect(() => {
    fetchLCShifts(localStorage.getItem('today') || moment.utc().format('YYYY-MM-DD'));
  }, []);

  const fetchLCShifts = async (dateYMD: string) => {
    setIsLoading(true);
    const response = await LiveClassBookingService.getAvailability({
      dateFrom: `${dateYMD}T00:00:00`,
      dateTo: `${dateYMD}T23:59:59`,
    });
    setIsLoading(false);
    setLiveClassShiftListItems(getLiveClassShiftListItemsFromRawData(response.data?.data));
  };

  const onClickDayItem = (dateYMD: string) => {
    fetchLCShifts(dateYMD);
  };

  const Loading = () => (
    <LoadingContainer>
      <img src={loadingGif} alt="Loading" />
    </LoadingContainer>
  );

  return (
    <>
      <StyledPanel
        variant="success"
        hidden={!showFinishedAllLevelClassesAlert}
        onHide={() => setShowFinishedAllLevelClassesAlert(false)}
      >
        <StyledPanelTitle>{__('cheers')}</StyledPanelTitle>
        <StyledPanelDescription>{__('finishedAllClassesInLevel')}</StyledPanelDescription>
      </StyledPanel>
      <StyledPanel
        variant="warning"
        hidden={!showNoPrivateClassesAvailableAlert}
        onHide={() => setShowNoPrivateClassesAvailableAlert(false)}
      >
        <StyledPanelTitle>{__('noPrivateClassesLeft')}</StyledPanelTitle>
        <StyledPanelDescription>
          {__('noMorePrivateClassesContinueLearningMessage')}
        </StyledPanelDescription>
      </StyledPanel>
      <ReservationDashboard />
      <StyledDaysCarousel onClickDayItem={onClickDayItem} />
      {isLoading ? <Loading /> : <LiveClassShiftList items={liveClassShiftListItems} />}
    </>
  );
};

const LoadingContainer = styled.div`
  width: 100%;
  height: 130px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledDaysCarousel = styled(DaysCarousel)`
  margin: 12px auto 16px;
  max-width: 438px;
`;

const StyledPanel = styled(Panel)`
  margin-bottom: 16px;
`;

const StyledPanelTitle = styled.p`
  font-weight: 700;
  line-height: 20px !important;
  font-size: 14px !important;
`;

const StyledPanelDescription = styled.p`
  font-size: 14px !important;
  line-height: 20px !important;
`;
